import { useMemo, useRef } from "react"

import { getRouteVariance, type RouteForStatus } from "@/lib/statusDisplay"
import type { Variance } from "@/lib/tone"
import { stabiliseAll, type VarianceState } from "@/lib/varianceStability"

/**
 * The variance band to show per route, smoothed so it doesn't flicker on a threshold.
 *
 * All the interesting logic is in lib/varianceStability so it can be tested without React; this
 * only owns the memory between renders. The ref rather than state is deliberate — the smoothed
 * result is derived from `now`, which the views already tick once a minute, so keeping it in
 * state would just add a second render for no new information.
 */
export function useStableVariance<T extends RouteForStatus & { id: string }>(
  routes: T[],
  now: Date,
): (route: T) => Variance | null {
  const memory = useRef<Map<string, VarianceState>>(new Map())

  return useMemo(() => {
    const observed = new Map<string, Variance | null>(
      routes.map((r) => [r.id, getRouteVariance(r, now)]),
    )
    memory.current = stabiliseAll(observed, memory.current, now)
    const settled = memory.current
    return (route: T) => settled.get(route.id)?.shown ?? null
  }, [routes, now])
}
